import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApiClient } from "../api/ApiClientContext";
import { useAuth } from "../auth/AuthContext";
import { LocalityMap, trackColour, type LocalityLayout } from "../components/LocalityMap";
import { localityFlights } from "../components/localityFlights";
import { teamColor } from "../components/teamColor";
import { TeamPill } from "../components/TeamPill";
import { editorNames } from "../components/EditorList";
import { AnnotationsApi } from "../api/annotations";
import type { FlightMeta, FlightSetRef, TeamSummaryEntry } from "../api/types";
import { listFlights as listOfflineFlights, getAsset } from "../offline/store";
import type { OfflineFlightRecord } from "../offline/schema";
import { OfflineFlightBadge } from "../components/OfflineFlightBadge";
import { useTourAutoStart } from "../tour/useTourAutoStart";

type FlightListItem = FlightMeta & { thumbUrl?: string; set?: FlightSetRef };

type State =
  | { kind: "loading" }
  | { kind: "ok"; flights: FlightListItem[]; teams: TeamSummaryEntry[] }
  // The Worker is unreachable but flights were downloaded for offline use —
  // list those from IndexedDB instead of stranding the operator on an error.
  // thumbs holds one object URL per flightId with a cached `:thumbnail`
  // asset; flights with no cached thumbnail are simply absent from the map.
  | { kind: "offline"; records: OfflineFlightRecord[]; thumbs: Record<string, string> }
  | { kind: "error"; message: string };

export function FlightsPage(): JSX.Element {
  const client = useApiClient();
  const { team: ownTeam } = useAuth();
  const isAdmin = ownTeam?.role === "admin";
  const [state, setState] = useState<State>({ kind: "loading" });
  // flightId|team -> editor names. Populated lazily after the flight list
  // resolves; empty array means "fetched, no editors yet".
  const [editorsByKey, setEditorsByKey] = useState<Record<string, string[]>>({});
  const annotationsApi = useMemo(() => new AnnotationsApi(client), [client]);
  const [cached, setCached] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  // `${team}/${flightId}` of the card or track under the pointer (spec §7.5).
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // Guided tour: the flights chapter offers itself once the list has resolved
  // (loaded, empty, error or the offline fallback), never over the loading text.
  useTourAutoStart("flights", state.kind !== "loading");

  useEffect(() => {
    void (async () => {
      const list = await listOfflineFlights();
      setCached(new Set(list.filter((r) => r.partial !== true).map((r) => r.flightId)));
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // In parallel — serialising these doubled the admin loading hang.
        // /api/teams answers every role: admin gets every client, a parent
        // gets its sub-clients, anyone else gets [] (spec 2026-09-03 §6.2).
        // A failed sub-client lookup degrades a parent to the flat list; it
        // must never hide the flights.
        const [flightsRes, teamsRes] = await Promise.all([
          client.listFlights(),
          client.listTeams().catch(() => ({ teams: [] })),
        ]);
        if (!cancelled) {
          setState({ kind: "ok", flights: flightsRes.flights, teams: teamsRes.teams });
        }
      } catch (err) {
        if (cancelled) return;
        // Only complete downloads are openable; a partial one has no usable
        // video/track blob (see resolveCachedFlight).
        const recs = (await listOfflineFlights()).filter((r) => r.partial !== true);
        if (cancelled) return;
        if (recs.length === 0) {
          setState({ kind: "error", message: (err as Error).message });
        } else {
          const thumbEntries = await Promise.all(
            recs.map(async (r) => {
              const asset = await getAsset(`${r.flightId}:thumbnail`);
              return asset !== null
                ? ([r.flightId, URL.createObjectURL(asset.blob)] as const)
                : null;
            }),
          );
          if (cancelled) {
            for (const e of thumbEntries) if (e !== null) URL.revokeObjectURL(e[1]);
            return;
          }
          const thumbs = Object.fromEntries(
            thumbEntries.filter((e): e is readonly [string, string] => e !== null),
          );
          setState({ kind: "offline", records: recs, thumbs });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  // Sectioned when the caller can see more than one team's scope: the admin
  // always, a parent when it has sub-clients. Links then carry ?team=.
  const sectioned = isAdmin || (state.kind === "ok" && state.teams.length > 0);

  // Fan-out fetch annotations per flight so each card can show editors.
  // Failures (404 / network) silently degrade to "no editors shown".
  useEffect(() => {
    if (state.kind !== "ok") return;
    let cancelled = false;
    for (const f of state.flights) {
      const key = `${f.team}/${f.flightId}`;
      const teamParam = sectioned ? f.team : undefined;
      annotationsApi
        .get(f.flightId, teamParam)
        .then((res) => {
          if (cancelled) return;
          const names = res.annotations !== null ? editorNames(res.annotations) : [];
          setEditorsByKey((m) => ({ ...m, [key]: names }));
        })
        .catch(() => {
          if (cancelled) return;
          setEditorsByKey((m) => ({ ...m, [key]: [] }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [state, annotationsApi, sectioned]);

  // Offline thumbnails are object URLs backed by IndexedDB blobs — revoke
  // them once they're no longer shown, whether by unmount or by leaving the
  // offline state (e.g. the network comes back on a later mount).
  useEffect(() => {
    if (state.kind !== "offline") return;
    const thumbs = state.thumbs;
    return () => {
      for (const url of Object.values(thumbs)) URL.revokeObjectURL(url);
    };
  }, [state]);

  if (state.kind === "loading") return <p>Loading flights…</p>;
  if (state.kind === "error") {
    return (
      <p role="alert" style={{ color: "#b00020" }}>
        {state.message}
      </p>
    );
  }

  // Offline we cannot know about sub-clients, so `sectioned` has already
  // collapsed to `isAdmin`; admin links still need ?team=.
  const flightHref = (f: FlightMeta): string =>
    sectioned
      ? `/flights/${encodeURIComponent(f.flightId)}?team=${encodeURIComponent(f.team)}`
      : `/flights/${encodeURIComponent(f.flightId)}`;

  // One section of the page: its cards and its locality map. The map (and
  // with it the card numbers) appears only once a flight carries the fields.
  // With enough cards to fill a column the map sits beside them as a tall
  // sticky panel; a short section stacks it above instead, because the
  // column's extra height would be empty imagery (layout CSS in
  // viewer-overrides.css, which also stacks on narrow windows).
  const renderSection = (flights: FlightListItem[]): JSX.Element => {
    const ordered = orderFlights(flights);
    const drawn = localityFlights(ordered, flightHref);
    const layout = sectionLayout(ordered.length);
    return (
      <div
        data-testid="flights-section"
        data-layout={layout}
        className={`vmc-flights-section vmc-flights-section--${layout}`}
      >
        <FlightCardList
          flights={ordered}
          hrefFor={flightHref}
          editorsByKey={editorsByKey}
          cached={cached}
          showIndex={drawn.length > 0}
          hoveredKey={hoveredKey}
          onHover={setHoveredKey}
        />
        {drawn.length > 0 && (
          <LocalityMap
            flights={drawn}
            hoveredKey={hoveredKey}
            onHoverChange={setHoveredKey}
            onOpen={(href) => navigate(href)}
            layout={layout}
          />
        )}
      </div>
    );
  };

  if (state.kind === "offline") {
    return (
      <main style={{ fontFamily: "system-ui", padding: "2rem" }}>
        <h1>Flights</h1>
        <p role="status" style={{ color: "#666" }}>
          Offline — showing downloaded flights only.
        </p>
        {/* No locality map offline: its tiles are Esri's and are never
            cached (spec §7.7), so the map would be a grey box. */}
        <FlightCardList
          flights={state.records.map((r) => ({ ...r.meta, thumbUrl: state.thumbs[r.flightId] }))}
          hrefFor={flightHref}
          editorsByKey={{}}
          cached={new Set(state.records.map((r) => r.flightId))}
          showIndex={false}
          hoveredKey={null}
          onHover={() => undefined}
        />
      </main>
    );
  }

  if (!sectioned) {
    if (state.flights.length === 0) return <p>No flights yet.</p>;
    return (
      <main style={{ fontFamily: "system-ui", padding: "2rem" }}>
        <h1>Flights</h1>
        {renderSection(state.flights)}
      </main>
    );
  }

  const byTeam = new Map<string, FlightListItem[]>();
  for (const f of state.flights) {
    const arr = byTeam.get(f.team) ?? [];
    arr.push(f);
    byTeam.set(f.team, arr);
  }
  const sortedTeams = state.teams
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  // A parent's own section comes first; the admin has no flights of its own.
  const sections: TeamSummaryEntry[] =
    !isAdmin && ownTeam !== null
      ? [{ slug: ownTeam.slug, displayName: ownTeam.displayName }, ...sortedTeams]
      : sortedTeams;

  return (
    <main style={{ fontFamily: "system-ui", padding: "2rem" }}>
      <h1>{isAdmin ? "Flights — Admin" : "Flights"}</h1>
      {sections.length === 0 && <p>No client teams yet.</p>}
      {sections.map((t) => {
        const flights = byTeam.get(t.slug) ?? [];
        const c = teamColor(t.slug);
        return (
          <details
            key={t.slug}
            open
            style={{ marginBottom: 16, border: "1px solid #ddd", borderRadius: 8 }}
          >
            <summary
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                cursor: "pointer",
                borderLeft: `6px solid ${c.bg}`,
                borderRadius: 8,
                listStyle: "none",
              }}
            >
              <span
                aria-hidden
                style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: c.bg }}
              />
              <strong>{t.displayName}</strong>
              <span style={{ color: "#666" }}>
                · {flights.length} flight{flights.length === 1 ? "" : "s"}
              </span>
            </summary>
            <div style={{ padding: "0 14px 14px" }}>
              {flights.length === 0 ? (
                <p style={{ color: "#888" }}>No flights for this client yet.</p>
              ) : (
                renderSection(flights)
              )}
            </div>
          </details>
        );
      })}
    </main>
  );
}

/** Sort key: when the flight was shot, not what its key happens to be.
 *
 *  R2 lists lexicographically by `flightId`, so a slug like
 *  `...-rec-4-09-38-36` sorts ahead of `...-rec1` ("-" < "1") and a set of
 *  recordings reads out of sequence. `videoStartUtc` is the exact start;
 *  pre-2026-08 flights predate it, so fall back to the coarser
 *  `captureDate` — both are ISO, so the same prefix comparison orders them.
 */
export function captureTimeKey(f: FlightMeta): string {
  return f.videoStartUtc ?? f.captureDate;
}

/** Cards it takes before the map earns its own column (spec rev 2, 2026-09-11). */
export const SIDE_LAYOUT_MIN_CARDS = 3;

export function sectionLayout(cardCount: number): LocalityLayout {
  return cardCount >= SIDE_LAYOUT_MIN_CARDS ? "side" : "stacked";
}

/** Card order: capture time, then `flightId` so the order is stable across
 *  refetches. The single ordering behind both the cards and the map numbers. */
export function orderFlights<T extends FlightMeta>(flights: T[]): T[] {
  return flights
    .slice()
    .sort(
      (a, b) =>
        captureTimeKey(a).localeCompare(captureTimeKey(b)) ||
        a.flightId.localeCompare(b.flightId),
    );
}

function FlightCardList({
  flights,
  hrefFor,
  editorsByKey,
  cached,
  showIndex,
  hoveredKey,
  onHover,
}: {
  flights: FlightListItem[];
  hrefFor: (f: FlightMeta) => string;
  editorsByKey: Record<string, string[]>;
  cached: Set<string>;
  /** Number the cards to match the section's locality map. */
  showIndex: boolean;
  hoveredKey: string | null;
  onHover: (key: string | null) => void;
}): JSX.Element {
  const ordered = orderFlights(flights);
  return (
    <ul
      data-tour="flights.list"
      style={{ listStyle: "none", padding: 0, display: "grid", gap: "1rem", margin: 0 }}
    >
      {ordered.map((f, i) => {
        const key = `${f.team}/${f.flightId}`;
        const editors = editorsByKey[key];
        const hovered = hoveredKey === key;
        const colour = trackColour(i + 1);
        return (
          <li key={key}>
            <Link
              to={hrefFor(f)}
              data-tour="flights.card"
              data-hover={hovered ? "true" : undefined}
              onMouseEnter={() => onHover(key)}
              onMouseLeave={() => onHover(null)}
              style={{
                display: "flex",
                gap: "1rem",
                padding: "0.75rem 1rem",
                border: "1px solid #ddd",
                borderRadius: 6,
                textDecoration: "none",
                color: "inherit",
                outline: hovered ? `2px solid ${colour}` : undefined,
                outlineOffset: 2,
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "relative",
                  width: 64,
                  height: 64,
                  background: "#eee",
                  borderRadius: 4,
                  flex: "0 0 auto",
                  overflow: "hidden",
                }}
              >
                {f.thumbUrl !== undefined && f.thumbUrl !== "" && (
                  <img
                    src={f.thumbUrl}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {showIndex && (
                  <span
                    data-testid="flight-index"
                    style={{
                      position: "absolute",
                      left: 3,
                      top: 3,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: colour,
                      border: "2px solid #fff",
                      boxSizing: "border-box",
                      color: "#000",
                      font: "700 11px/16px system-ui, sans-serif",
                      textAlign: "center",
                    }}
                  >
                    {i + 1}
                  </span>
                )}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{f.name}</div>
                <div style={{ color: "#666", fontSize: "0.9em" }}>{f.captureDate}</div>
                {editors !== undefined && editors.length > 0 && (
                  <div
                    style={{
                      color: "#666",
                      fontSize: "0.85em",
                      marginTop: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Editors: {editors.join(", ")}
                  </div>
                )}
                {f.set !== undefined && (
                  <span
                    style={{
                      display: "inline-block",
                      fontSize: "0.78em",
                      color: "#555",
                      border: "1px solid #ccc",
                      borderRadius: 999,
                      padding: "1px 8px",
                      marginTop: 4,
                      marginRight: 6,
                    }}
                  >
                    {`${f.set.name} · ${f.set.position} of ${f.set.size}`}
                  </span>
                )}
                {f.sharedFrom !== undefined && (
                  <span style={{ display: "inline-block", marginTop: 4, marginRight: 6 }}>
                    <TeamPill
                      slug={f.sharedFrom.slug}
                      displayName={`Shared by ${f.sharedFrom.displayName}`}
                    />
                  </span>
                )}
                <OfflineFlightBadge isCached={cached.has(f.flightId)} />
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
